import { remove } from 'lodash';
import { Injectable } from '@angular/core';
import { Observable, Subscription, of } from 'rxjs';
import {
  UserPreferences,
  METADATA_KEY,
  FEATURE,
  GROUP,
} from '@models/user-preferences';
import { UserPreferencesService } from './userPreferences.service';
import { MigrateUserPreferencesService } from './migrateUserPreferences.service';
import {
  Watchlist,
  WatchlistIssuerVM,
  WatchlistVM,
} from '@models/watch-list.class';
import { map } from 'rxjs/operators';
import { MarketType, Markets } from 'src/solace';

@Injectable()
export class MonitorWatchlistService {
  private preferences: UserPreferences[];
  private watchlists: WatchlistVM[];

  constructor(
    private migrateWatchlist: MigrateUserPreferencesService,
    private userPreferences: UserPreferencesService,
  ) {
    this.watchlists = [];
    this.preferences = [];
  }

  subscribeToWatchlists(): Observable<WatchlistVM[]> {
    return this.migrateWatchlist.migrate().pipe(
      map((response) => {
        this.preferences = response;
        this.watchlists = Watchlist.getWatchlists(response);
        return this.watchlists;
      }),
    );
  }

  getWatchlistDetail(id: number): Observable<WatchlistIssuerVM[]> {
    const { preference } = this.getWatchlistData(id);

    const metaIssuers = Watchlist.getMetaDataByKey(
      preference,
      METADATA_KEY.issuers,
    );

    const issuers = Watchlist.parseIssuers(
      metaIssuers ? metaIssuers.value ?? '' : '',
    );

    return of(
      issuers.map(([securityId, marketId]) => {
        return {
          watchlistType: id,
          issueID: securityId,
          marketID: MarketType[marketId],
        };
      }),
    );
  }

  addWatchlist(name: string, _configuration: string): Observable<any> {
    const orderIds = this.watchlists
      .map((watchlist) => watchlist.watchListTypeId)
      .sort();

    const nextOrderId = orderIds[orderIds.length - 1] + 1;

    const preference: UserPreferences = {
      group_id: `${GROUP.watchlist}${nextOrderId}`,
      metadata: [
        {
          key: METADATA_KEY.name,
          value: name ?? 'Watchlist',
        },
        {
          key: METADATA_KEY.order,
          value: `${nextOrderId}`,
        },
        {
          key: METADATA_KEY.isMigrated,
          value: 'true',
        },
        {
          key: METADATA_KEY.issuers,
          value: '',
        },
      ],
    };

    return this.userPreferences
      .createPreference(FEATURE.watchlist, preference)
      .pipe(
        map((response) => {
          this.watchlists.push({
            watchListTypeId: nextOrderId,
            preferenceId: response.id,
            configuration: '',
            title: name,
          });

          this.preferences.push(response);

          return { response: nextOrderId };
        }),
      );
  }

  renameWatchList(
    watchlistId: number,
    newName: string,
    _configuration?: string,
  ): Observable<any> {
    const { preference, watchlist } = this.getWatchlistData(watchlistId);

    preference.metadata = Watchlist.updateMetaData({
      metaData: preference.metadata,
      metaKey: METADATA_KEY.name,
      metaValue: newName,
    });

    return this.userPreferences
      .updatePreference(FEATURE.watchlist, preference.id, preference)
      .pipe(
        map((response) => {
          this.updateData(watchlist.watchListTypeId, {
            name: newName,
            preference: response,
          });

          return {};
        }),
      );
  }

  removeWatchlist(watchlistId: number): Observable<any> {
    const { preference, watchlist } = this.getWatchlistData(watchlistId);

    return this.userPreferences
      .deletePreference(FEATURE.watchlist, preference.id)
      .pipe(
        map(() => {
          this.updateData(watchlist.watchListTypeId, {
            removePreference: true,
          });

          return {};
        }),
      );
  }

  removeIssue(
    watchlistId: number,
    issueString: string,
    marketId: number,
  ): Observable<any> {
    const { preference } = this.getWatchlistData(watchlistId);

    const metaIssuers = Watchlist.getMetaDataByKey(
      preference,
      METADATA_KEY.issuers,
    );

    let issuers = Watchlist.parseIssuers(metaIssuers ? metaIssuers.value : '');

    const marketString = Markets[marketId].typeString;

    issuers = issuers.filter(([securityId, tmpMarketId]) => {
      return !(
        securityId === issueString &&
        tmpMarketId.toLowerCase() === marketString.toLowerCase()
      );
    });

    preference.metadata = Watchlist.updateMetaData({
      metaData: preference.metadata,
      metaKey: METADATA_KEY.issuers,
      metaValue: issuers.map((item) => item.join('|')).join(','),
    });

    return this.userPreferences
      .updatePreference(FEATURE.watchlist, preference.id, preference)
      .pipe(
        map((response) => {
          this.updateData(watchlistId, {
            preference: response,
          });

          return {};
        }),
      );
  }

  pushNewIssueInWatchlist(
    issue: string,
    marketId: 0 | 2 | 90,
    watchlistId: number,
  ): Observable<any> {
    const { preference } = this.getWatchlistData(watchlistId);

    const metaIssuers = Watchlist.getMetaDataByKey(
      preference,
      METADATA_KEY.issuers,
    );

    const issuers = Watchlist.parseIssuers(
      metaIssuers ? metaIssuers.value : '',
    );

    issuers.push([issue, Markets[marketId].typeString]);

    preference.metadata = Watchlist.updateMetaData({
      metaData: preference.metadata,
      metaKey: METADATA_KEY.issuers,
      metaValue: issuers.map((item) => item.join('|')).join(','),
    });

    return this.userPreferences
      .updatePreference(FEATURE.watchlist, preference.id, preference)
      .pipe(
        map((response) => {
          this.updateData(watchlistId, {
            preference: response,
          });
          return {};
        }),
      );
  }

  private getWatchlistData(id: number): {
    watchlist: WatchlistVM;
    preference: UserPreferences;
  } {
    const watchlist = this.watchlists.find(
      (tmpWatchlist) => tmpWatchlist.watchListTypeId === id,
    );

    const preference = this.preferences.find(
      (tmpPreference) => tmpPreference.id === watchlist.preferenceId,
    );

    return {
      watchlist,
      preference,
    };
  }

  private updateData(
    watchlistId: number,
    {
      name,
      preference,
      removePreference,
    }: {
      name?: string;
      preference?: UserPreferences;
      removePreference?: boolean;
    },
  ): void {
    if (removePreference) {
      const watchlist = this.watchlists.find(
        (tmpWatchlist) => tmpWatchlist.watchListTypeId === watchlistId,
      );

      this.watchlists = this.watchlists.filter(
        (tmpWatchlist) =>
          tmpWatchlist.watchListTypeId !== watchlist.watchListTypeId,
      );

      this.preferences = this.preferences.filter(
        (tmpPreference) => tmpPreference.id !== watchlist.preferenceId,
      );

      return;
    }

    if (name) {
      this.watchlists = this.watchlists.map((tmpWatchlist) => {
        if (tmpWatchlist.watchListTypeId === watchlistId) {
          return {
            ...tmpWatchlist,
            title: name,
          };
        }
        return tmpWatchlist;
      });
    }

    if (preference) {
      this.preferences = this.preferences.map((tmpPreference) => {
        if (tmpPreference.id === preference.id) {
          return {
            ...preference,
          };
        }
        return tmpPreference;
      });
    }
  }
}
